import { useEffect } from 'react';
import { Link } from 'react-router-dom';

const sections = [
  {
    number: '1',
    title: 'Datos que recopilamos',
    content: (
      <div>
        <p className="mb-3">Podemos recopilar:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Nombre</li>
          <li>Número de teléfono</li>
          <li>Dirección de envío</li>
          <li>Correo electrónico</li>
        </ul>
      </div>
    ),
  },
  {
    number: '2',
    title: 'Finalidad del uso',
    content: (
      <div>
        <p className="mb-3">Usamos tus datos para:</p>
        <ul className="list-disc list-inside space-y-1">
          <li>Procesar pedidos</li>
          <li>Coordinar envíos</li>
          <li>Brindar atención al cliente</li>
          <li>Compartir información sobre promociones (con autorización)</li>
        </ul>
      </div>
    ),
  },
  {
    number: '3',
    title: 'Protección de la información',
    content: (
      <p>
        Adoptamos medidas razonables para proteger tus datos y evitar accesos no autorizados.
      </p>
    ),
  },
  {
    number: '4',
    title: 'Compartir información',
    content: (
      <div>
        <p className="mb-3">
          No vendemos ni compartimos tus datos personales, excepto cuando sea necesario para:
        </p>
        <ul className="list-disc list-inside space-y-1">
          <li>Envíos (transportadoras)</li>
          <li>Procesamiento de pagos</li>
        </ul>
      </div>
    ),
  },
  {
    number: '5',
    title: 'Derechos del usuario',
    content: (
      <div>
        <p className="mb-3">Como titular de los datos, puedes:</p>
        <ul className="list-disc list-inside space-y-1 mb-4">
          <li>Solicitar acceso a tu información</li>
          <li>Pedir corrección o actualización</li>
          <li>Solicitar eliminación de tus datos</li>
        </ul>
        <p>
          Puedes hacerlo escribiendo a:{' '}
          <a
            href="mailto:sotovalderramalaurayisel@gmail.com"
            className="text-amber-600 hover:underline"
          >
            sotovalderramalaurayisel@gmail.com
          </a>
        </p>
      </div>
    ),
  },
  {
    number: '6',
    title: 'Uso de cookies',
    content: (
      <p>Este sitio puede usar cookies para mejorar la experiencia del usuario.</p>
    ),
  },
  {
    number: '7',
    title: 'Cambios en la política',
    content: (
      <p>Podemos actualizar esta política en cualquier momento.</p>
    ),
  },
  {
    number: '8',
    title: 'Ubicación',
    content: (
      <p>Operamos desde Villavicencio, Colombia.</p>
    ),
  },
];

export default function PrivacyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);

    document.title = 'Política de Privacidad | Laura Soto Shop';

    const existing = document.getElementById('ld-privacy');
    if (existing) existing.remove();

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "name": "Política de Privacidad — Laura Soto Shop",
      "description": "Política de privacidad y protección de datos personales de Laura Soto Shop, conforme a la normativa colombiana.",
      "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/privacidad`,
      "inLanguage": "es",
      "isPartOf": {
        "@type": "WebSite",
        "name": "Laura Soto Shop",
        "url": `${import.meta.env.VITE_SITE_URL ?? 'https://example.com'}/`
      }
    };

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'ld-privacy';
    script.text = JSON.stringify(jsonLd);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById('ld-privacy');
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-stone-900 text-white py-16 px-6">
        <div className="max-w-3xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-amber-400 hover:text-amber-300 text-sm mb-8 transition-colors cursor-pointer"
          >
            <i className="ri-arrow-left-line" />
            Volver al inicio
          </Link>
          <p className="text-xs tracking-[0.2em] text-amber-400 uppercase mb-3 font-sans">
            Laura Soto Shop
          </p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold mb-4">
            Política de Privacidad
          </h1>
          <p className="text-stone-400 text-sm font-sans">
            En Laura Soto Shop respetamos y protegemos los datos personales de nuestros clientes,
            conforme a la normativa aplicable en Colombia.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-3xl mx-auto px-6 py-16">
        {/* Trust badge */}
        <div className="flex items-center gap-4 bg-emerald-50 border border-emerald-200 rounded-xl px-6 py-4 mb-12">
          <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
            <i className="ri-shield-check-fill text-emerald-500 text-2xl" />
          </div>
          <div>
            <p className="font-sans font-semibold text-emerald-800 text-sm">
              Tus datos están seguros con nosotros
            </p>
            <p className="font-sans text-xs text-emerald-700 mt-0.5">
              Nunca vendemos ni compartimos tu información personal con terceros no autorizados.
            </p>
          </div>
        </div>

        <div className="space-y-10">
          {sections.map((sec) => (
            <div key={sec.number} className="border-b border-stone-100 pb-10 last:border-0">
              <div className="flex items-start gap-5">
                <span className="flex-shrink-0 w-9 h-9 rounded-full bg-amber-50 border border-amber-200 flex items-center justify-center font-sans font-bold text-amber-600 text-sm">
                  {sec.number}
                </span>
                <div className="flex-1 pt-1">
                  <h2 className="font-serif text-xl font-bold text-stone-900 mb-3">{sec.title}</h2>
                  <div className="font-sans text-sm text-stone-600 leading-relaxed">
                    {sec.content}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="mt-16 bg-amber-50 border border-amber-200 rounded-2xl p-8 text-center">
          <p className="font-serif text-xl font-bold text-stone-900 mb-2">
            ¿Tienes preguntas sobre tu privacidad?
          </p>
          <p className="font-sans text-sm text-stone-600 mb-6">
            Escríbenos y gestionamos tu solicitud de forma inmediata.
          </p>
          <a
            href="mailto:sotovalderramalaurayisel@gmail.com"
            className="inline-flex items-center gap-2 bg-stone-900 hover:bg-stone-700 text-white font-sans text-sm font-semibold px-8 py-3 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-mail-line text-lg" />
            sotovalderramalaurayisel@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}
